import { FC, useMemo, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import { t } from 'i18next';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { useSTKContext } from '@/context/context';
import styles from './PriceChangeConfirmation.module.css';
import { NamedPriceChangeConfirmationProps } from './NamedPriceChangeConfirmationProps.types';

export const NamedPriceChangeConfirmation: FC<NamedPriceChangeConfirmationProps> = ({
  isOpen,
  onClose,
  onCancel,
  onSubmit,
  newPrice,
  namedPriceRows,
}) => {
  const { storesAndGroups } = useSTKContext();

  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    if (storesAndGroups.origin === 'tab-store') {
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet).sort();
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      return (storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || []).sort();
    }

    return [];
  }, [storesAndGroups]);

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  const formattedNewPrice = `$${parseFloat(newPrice || '0').toFixed(2)}`;

  return (
    <SwipeableDrawer open={isOpen} onClose={onClose} onOpen={() => undefined} anchor="bottom">
      <div className={styles.container}>
        <header>
          <div>
            <h1>{t('editMenuPrice.activateEditMenuPrice')}</h1>
          </div>
          <button type="button" onClick={onClose}>
            <X data-testid="x-icon" size={24} color="var(--black-900)" />
          </button>
        </header>

        <div className={styles.content}>
          <div className={styles.confirmationContent}>
            <h2 className={styles.confirmationTitle}>{t('editMenuPrice.submitMenuChanges')}</h2>
            {namedPriceRows.length > 0 && namedPriceRows.every((row) => row.isZeroPriced === true) && (
              <p className={styles.confirmationInfo}>{t('editMenuPrice.zeroPricedModifierConfirmationInfo')}</p>
            )}

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.productHeader}>{t('editMenuPrice.modifierColumn')}</th>
                    {allStoreGroupNames.map((storeGroupName) => {
                      const { count, total } = getStoreGroupsCount(storeGroupName);
                      return (
                        <th key={storeGroupName} className={styles.priceHeader}>
                          <div className={styles.headerWithCount}>
                            <div className={styles.headerName}>{storeGroupName}</div>
                            <div className={styles.headerCount}>
                              {count} / {total}
                            </div>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {namedPriceRows.map((row) => (
                    <tr key={row.namedPriceId}>
                      <td className={styles.productCell}>
                        <div className={styles.productColumnContent}>
                          <div className={`${styles.productName} ${styles.namedPriceName}`}>{row.name}</div>
                        </div>
                      </td>
                      {allStoreGroupNames.map((storeGroupName) => (
                        <td key={storeGroupName} className={styles.priceCell}>
                          {formattedNewPrice}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.alertContainer}>
              <CalendarAlert requestedVerbiage={true} />
            </div>

            <div className={styles.buttonContainer}>
              <Button
                className={styles.cancelButton}
                onClick={onCancel}
                variant={ButtonVariants.Secondary}
                type="button"
              >
                {t('editMenuPrice.cancel')}
              </Button>
              <Button
                className={styles.submitButton}
                data-testid="named-price-submit"
                onClick={onSubmit}
                variant={ButtonVariants.Primary}
                type="button"
              >
                {t('editMenuPrice.submitChanges')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </SwipeableDrawer>
  );
};

export default NamedPriceChangeConfirmation;
